import type { DocPackInfo } from '@listening/api-contract/schemas';
import { HttpClient } from 'effect/http';
import {
  DOC_PACK_CONTENT_VERSION,
  type DocPackCatalog,
  type DocPackCorruptError,
  type DocPackPage,
  type DocPackRangeFetchError,
  docPackPageByteRange,
  isDocPackCatalog,
  isDocPackPage,
} from '@listening/doc-pack';
import {
  decodeDocPackPageBlob,
  fetchDocPackByteRange,
  fetchDocPackPages,
  openDocPackCatalogFromUrl,
  type LoadedDocPack,
} from '@listening/doc-pack/effect';
import { useAtomSet, useAtomValue } from '@effect/atom-react';
import {
  Cache,
  Data,
  Duration,
  Effect,
  Exit,
  Option,
  Record,
  Request,
  RequestResolver,
  Schedule,
  Schema,
} from 'effect';
import { Atom, type AtomRegistry } from 'effect/reactivity';
import { ssrApiQuery } from '../query/api-runtime';
import { preloadAtom, resolveAtom } from '../query/load-atom';
import {
  buildDocPackReadingChunks,
  docPackCatalogPageReadableCounts,
  findDocPackChunkContaining,
  type DocPackReadingAnchor,
  type DocPackReadingClassOverrides,
} from './reading-plan';

const FULL_BLOB_LIMIT_BYTES = 4 * 1024 * 1024;
export type DocPackPageFetchMode = 'auto' | 'range';

// The ranged-read protocol (catalog-open ritual, range semantics, typed
// errors, timeout) lives in @listening/doc-pack/effect — shared with the
// backend readers. This file keeps the atoms readers load packs through,
// prefetch and read-ahead, and the small-pack full-blob cache.
export type { LoadedDocPack } from '@listening/doc-pack/effect';
export {
  DocPackCorruptError,
  DocPackPageAbsentError,
  DocPackRangeFetchError,
} from '@listening/doc-pack';

type DocPackReadError = DocPackRangeFetchError | DocPackCorruptError;

/** Pack reads go straight to the bucket, so a dropped connection or a "try
 * later" status is read once more, as the API's own pack reads do. */
const retryBucketReadOnce = Effect.updateService(
  HttpClient.HttpClient,
  HttpClient.retryTransient({
    times: 1,
    schedule: Schedule.exponential('100 millis').pipe(Schedule.jittered),
  }),
);

// These effects require the HttpClient service — every app runtime provides
// FetchHttpClient.layer (see AppRuntime in ../query/runtime-context), so the
// requirement is satisfied at the eq/runtime boundary, not here.
export const fetchDocPackCatalogEffect = (
  url: string,
): Effect.Effect<LoadedDocPack, DocPackReadError, HttpClient.HttpClient> =>
  openDocPackCatalogFromUrl(url);

const LoadedDocPackSchema = Schema.Struct({
  url: Schema.String,
  blobBaseOffset: Schema.Finite,
  catalog: Schema.declare(isDocPackCatalog),
});

/** A pack's catalog. It never changes, so it is never refetched; the public
 * doc page renders it on the server. */
export const docPackCatalogAtom = Atom.family(
  ({ audioId, url }: { readonly audioId: string; readonly url: string }) =>
    ssrApiQuery(fetchDocPackCatalogEffect(url).pipe(retryBucketReadOnce), {
      key: `doc-pack-catalog:${audioId}:${url}`,
      success: LoadedDocPackSchema,
      staleTime: Duration.infinity,
    }),
);

/** A reading chunk found from an anchor: built from the pack's pages and
 * fixed for one pack, page fetch mode and set of reading classes. */
export class DocPackChunkFrom extends Data.Class<{
  readonly audioId: string;
  readonly packUrl: string;
  readonly fetchMode: DocPackPageFetchMode;
  readonly classOverrides: DocPackReadingClassOverrides;
  readonly splitAtSkippedSegments: boolean;
  readonly page: number;
  readonly charStart: number;
}> {}

const EMPTY_PAGE = (page: number): DocPackPage => ({
  page,
  size: [612, 792],
  textSource: 'ocr',
  degraded: false,
  text: '',
  words: [],
  sentences: [],
  blocks: [],
  segments: [],
});

const totalBlobBytes = (catalog: DocPackCatalog) => {
  let max = 0;
  for (const page of catalog.pages) {
    if (page.offset + page.length > max) max = page.offset + page.length;
  }
  return max;
};

/** A successful read is kept; a failed one is not, so the next reader asks
 * again. */
const keepSuccesses = (exit: Exit.Exit<unknown, unknown>) =>
  Exit.isSuccess(exit) ? Duration.infinity : Duration.zero;

class DocPackBlobRegion extends Data.Class<{
  readonly url: string;
  readonly start: number;
  readonly endInclusive: number;
}> {}

/** Whole-blob downloads of the two most recent small packs. Concurrent readers
 * share one download, and each reader's runtime provides the HttpClient. */
const fullBlobCache = Effect.runSync(
  Cache.makeWith(
    (region: DocPackBlobRegion) =>
      fetchDocPackByteRange(region.url, region.start, region.endInclusive),
    {
      capacity: 2,
      timeToLive: keepSuccesses,
      requireServicesAt: 'lookup',
    },
  ),
);

export const resetDocPackBlobCacheForTesting = () =>
  Effect.runSync(Cache.invalidateAll(fullBlobCache));

export const fullDocPackBlobRegionEffect = (pack: LoadedDocPack) =>
  Cache.get(
    fullBlobCache,
    new DocPackBlobRegion({
      url: pack.url,
      start: pack.blobBaseOffset,
      endInclusive: pack.blobBaseOffset + totalBlobBytes(pack.catalog) - 1,
    }),
  );

export function decodeDocPackPageFromRegionEffect(
  pack: LoadedDocPack,
  region: Uint8Array,
  page: number,
): Effect.Effect<DocPackPage, DocPackCorruptError> {
  const range = docPackPageByteRange(pack.catalog, pack.blobBaseOffset, page);
  if (!range) return Effect.succeed(EMPTY_PAGE(page));
  const blob = region.subarray(
    range.start - pack.blobBaseOffset,
    range.endInclusive + 1 - pack.blobBaseOffset,
  );
  return decodeDocPackPageBlob(pack.url, page, blob);
}

class DocPackPageRead extends Request.Class<
  { readonly pack: LoadedDocPack; readonly page: number },
  DocPackPage,
  DocPackReadError,
  HttpClient.HttpClient
> {}

const completePageReads = (
  entries: ReadonlyArray<Request.Entry<DocPackPageRead>>,
  exit: Exit.Exit<ReadonlyMap<number, DocPackPage>, DocPackReadError>,
) => {
  for (const entry of entries) {
    const { page } = entry.request;
    entry.completeUnsafe(
      Exit.map(exit, (pages) => pages.get(page) ?? EMPTY_PAGE(page)),
    );
  }
};

/** Page reads of one pack made together become one `fetchDocPackPages`, which
 * merges near ranges into one request: a read-ahead window is one round trip. */
const docPackPageReads = RequestResolver.makeGrouped<DocPackPageRead, string>({
  key: (entry) => entry.request.pack.url,
  resolver: (entries) => {
    const [first] = entries;
    return fetchDocPackPages(
      first.request.pack,
      entries.map((entry) => entry.request.page),
    ).pipe(
      Effect.provideContext(first.context),
      Effect.exit,
      Effect.map((exit) => completePageReads(entries, exit)),
    );
  },
});

export function fetchDocPackPageEffect(
  pack: LoadedDocPack,
  page: number,
  options?: { fetchMode?: DocPackPageFetchMode },
): Effect.Effect<DocPackPage, DocPackReadError, HttpClient.HttpClient> {
  const range = docPackPageByteRange(pack.catalog, pack.blobBaseOffset, page);
  if (!range) return Effect.succeed(EMPTY_PAGE(page));
  if (
    options?.fetchMode !== 'range' &&
    totalBlobBytes(pack.catalog) <= FULL_BLOB_LIMIT_BYTES
  ) {
    return fullDocPackBlobRegionEffect(pack).pipe(
      Effect.flatMap((region) =>
        decodeDocPackPageFromRegionEffect(pack, region, page),
      ),
    );
  }
  return Effect.request(new DocPackPageRead({ pack, page }), docPackPageReads);
}

const pageIndexKey = (packUrl: string, page: number) =>
  `${packUrl}#${page.toString()}`;

/**
 * Every decoded page the client holds, by pack and page. A page joins when its
 * atom loads it or a read-ahead fetches it, and leaves when its atom is
 * dropped, so readers can search loaded pages without fetching any.
 */
const docPackPagesIndexAtom = Atom.make<Readonly<Record<string, DocPackPage>>>(
  {},
).pipe(Atom.keepAlive);

/** Forgets every decoded page and found chunk, as if they had aged out. A page
 * atom still holding its page reads it again once refreshed. */
export const forgetDocPackReadsForTesting = (
  registry: AtomRegistry.AtomRegistry,
) =>
  Effect.suspend(() => {
    registry.set(docPackPagesIndexAtom, {});
    return Cache.invalidateAll(registry.get(docPackChunkFromCacheAtom));
  });

/** Records decoded pages as loaded, so page reads and read-ahead find them. */
export const indexDocPackPages = (
  registry: AtomRegistry.AtomRegistry,
  packUrl: string,
  pages: Iterable<readonly [number, DocPackPage]>,
) =>
  registry.update(docPackPagesIndexAtom, (index) => ({
    ...index,
    ...Object.fromEntries(
      [...pages].map(([page, decoded]) => [
        pageIndexKey(packUrl, page),
        decoded,
      ]),
    ),
  }));

export type DocPackPageKey = {
  readonly audioId: string;
  readonly packUrl: string;
  readonly page: number;
  readonly fetchMode?: DocPackPageFetchMode;
};

/**
 * One decoded page. A page never changes, so it is never refetched, but a long
 * document has many, so each is dropped two minutes after it leaves the
 * screen. A page a read-ahead already fetched loads without a request. The
 * public doc page renders the first page on the server.
 */
export const docPackPageAtom = Atom.family((key: DocPackPageKey) =>
  ssrApiQuery(
    (get) =>
      Effect.gen(function* () {
        const pack = yield* get.result(
          docPackCatalogAtom({ audioId: key.audioId, url: key.packUrl }),
        );
        const indexKey = pageIndexKey(key.packUrl, key.page);
        const loaded = Option.fromNullishOr(
          get.registry.get(docPackPagesIndexAtom)[indexKey],
        );
        const page = Option.isSome(loaded)
          ? loaded.value
          : yield* fetchDocPackPageEffect(pack, key.page, {
              fetchMode: key.fetchMode ?? 'auto',
            }).pipe(retryBucketReadOnce);
        indexDocPackPages(get.registry, key.packUrl, [[key.page, page]]);
        yield* Effect.addFinalizer(() =>
          Effect.sync(() =>
            get.registry.update(docPackPagesIndexAtom, (index) =>
              Record.remove(index, indexKey),
            ),
          ),
        );
        return page;
      }),
    {
      key: `doc-pack-page:${key.audioId}:${key.packUrl}:${key.page.toString()}:${key.fetchMode ?? 'auto'}`,
      success: Schema.declare(isDocPackPage),
      staleTime: Duration.infinity,
      timeToLive: Duration.minutes(2),
    },
  ),
);

/** The loaded pages among `pages` of a pack, without fetching any. */
export const loadedDocPackPagesAtom = Atom.family(
  ({
    packUrl,
    pages,
  }: {
    readonly packUrl: string;
    readonly pages: ReadonlyArray<number>;
  }) =>
    Atom.make((get) => {
      const index = get(docPackPagesIndexAtom);
      return pages.flatMap((page) => index[pageIndexKey(packUrl, page)] ?? []);
    }).pipe(
      Atom.withEquality(
        (previous: ReadonlyArray<DocPackPage>, next) =>
          previous.length === next.length &&
          previous.every((page, i) => page === next[i]),
      ),
    ),
);

/** Starts loading `pages` of a pack in the background. */
export function prefetchDocPackPages(
  registry: AtomRegistry.AtomRegistry,
  key: Omit<DocPackPageKey, 'page'>,
  pageCount: number,
  pages: ReadonlyArray<number>,
) {
  for (const page of pages) {
    if (page < 1 || page > pageCount) continue;
    void preloadAtom(registry, docPackPageAtom({ ...key, page }));
  }
}

/** Readable candidate pages warmed per batch during a chunk scan. */
const DOC_PACK_SCAN_BATCH_PAGES = 8;

/**
 * Scan forward from `startPage` for the stable chunk to play at `anchor`,
 * consulting the catalog so pages with zero readable chars (under the active
 * class overrides) are SKIPPED without any fetch — a seek into a trailing
 * non-readable region then costs zero round trips instead of one per page.
 *
 * The scan blocks on exactly ONE page at a time — the candidate it needs to
 * inspect — via `loadPage` (the page atoms). When that page yields
 * the chunk it returns immediately, never touching later pages. Only when a
 * page does NOT yield the chunk does it kick off `warmPages` for the next
 * window in the BACKGROUND (fire-and-forget), so a run of readable pages can
 * coalesce into one range request without this resolution ever blocking on
 * pages beyond the one it needs.
 *
 * Determinism: the returned chunk is identical to a naive page-by-page scan.
 * A zero-readable page can never yield a chunk — its catalog readable count is
 * 0 exactly when `buildDocPackReadingChunks` would emit nothing (both honor
 * the same class-enablement policy) — so skipping it changes only the fetch
 * pattern, never the result; and readable candidates are visited in ascending
 * page order, so the first match is the same page the naive scan returns.
 */
export const findDocPackChunkByScan = <E>(params: {
  readonly catalog: DocPackCatalog;
  readonly classOverrides: DocPackReadingClassOverrides;
  readonly splitAtSkippedSegments?: boolean | undefined;
  readonly anchor: DocPackReadingAnchor;
  readonly startPage: number;
  readonly loadPage: (pageNumber: number) => Effect.Effect<DocPackPage, E>;
  readonly warmPages?: (pageNumbers: readonly number[]) => void;
  readonly batchPages?: number;
}) =>
  Effect.gen(function* () {
    const {
      catalog,
      classOverrides,
      splitAtSkippedSegments,
      anchor,
      startPage,
    } = params;
    const batchPages = params.batchPages ?? DOC_PACK_SCAN_BATCH_PAGES;
    const candidates = catalog.pages
      .filter(
        (page) =>
          page.n >= startPage &&
          page.n <= catalog.pageCount &&
          docPackCatalogPageReadableCounts(page, classOverrides).chars > 0,
      )
      .map((page) => page.n)
      // Ascending page order matches the naive scan.
      .toSorted((a, b) => a - b);

    let warmedThrough = -1;
    for (let i = 0; i < candidates.length; i++) {
      const pageNumber = candidates[i];
      if (pageNumber === undefined) continue;
      const page = yield* params.loadPage(pageNumber);
      const chunk = findDocPackChunkContaining(
        buildDocPackReadingChunks([page], {
          classOverrides,
          splitAtSkippedSegments,
        }),
        anchor,
      );
      if (chunk) return chunk;
      // Not the chunk's page — more candidates to scan. Warm a window in the
      // background so upcoming loads batch, but never wait on it: this
      // resolution only ever blocks on the page it needs. Start the window one
      // past the page the loop loads next (candidates[i + 1]), which the
      // loadPage above fetches on its own on the very next iteration.
      const windowStart = i + 2;
      if (params.warmPages && windowStart > warmedThrough) {
        const window = candidates.slice(windowStart, windowStart + batchPages);
        if (window.length > 0) {
          warmedThrough = windowStart + window.length - 1;
          params.warmPages(window);
        }
      }
    }
    return undefined;
  });

const DOC_PACK_CHUNKS_FROM_CAPACITY = 64;

/** The chunk at a `DocPackChunkFrom` anchor, scanning forward from its page. */
const readDocPackChunkFrom = (
  registry: AtomRegistry.AtomRegistry,
  key: DocPackChunkFrom,
) =>
  Effect.gen(function* () {
    const pack = yield* resolveAtom(
      registry,
      docPackCatalogAtom({ audioId: key.audioId, url: key.packUrl }),
    );
    const pageKey = {
      audioId: key.audioId,
      packUrl: pack.url,
      fetchMode: key.fetchMode,
    };
    return yield* findDocPackChunkByScan({
      catalog: pack.catalog,
      classOverrides: key.classOverrides,
      splitAtSkippedSegments: key.splitAtSkippedSegments,
      anchor: { page: key.page, charStart: key.charStart },
      startPage: key.page,
      loadPage: (page) =>
        resolveAtom(registry, docPackPageAtom({ ...pageKey, page })),
      ...(key.fetchMode === 'range'
        ? {
            warmPages: (pages: readonly number[]) =>
              prefetchDocPackPages(
                registry,
                pageKey,
                pack.catalog.pageCount,
                pages,
              ),
          }
        : {}),
    });
  });

/** Chunks found from anchors, so the prefetch walk and the boundary advance
 * share one read and a chunk already found answers at once. Bounded; a failed
 * read is not kept. */
const docPackChunkFromCacheAtom = Atom.readable((get) =>
  Effect.runSync(
    Cache.makeWith(
      (key: DocPackChunkFrom) => readDocPackChunkFrom(get.registry, key),
      { capacity: DOC_PACK_CHUNKS_FROM_CAPACITY, timeToLive: keepSuccesses },
    ),
  ),
).pipe(Atom.keepAlive);

export const docPackChunkFrom = (
  registry: AtomRegistry.AtomRegistry,
  key: DocPackChunkFrom,
) => Cache.get(registry.get(docPackChunkFromCacheAtom), key);

/** A chunk already found from `key`; reading it never waits. */
export const cachedDocPackChunkFrom = (
  registry: AtomRegistry.AtomRegistry,
  key: DocPackChunkFrom,
) => Cache.getSuccess(registry.get(docPackChunkFromCacheAtom), key);

/** The advertised pack is one this client can decode. Takes the wire DTO
 * directly — no camelCase remap in between. */
export const isDocPackVersionUsable = (
  docPack: DocPackInfo | undefined | null,
): boolean => docPack?.content_version === DOC_PACK_CONTENT_VERSION;

export type DownloadedAudio = {
  readonly voice: string;
  readonly docPack?: DocPackInfo | undefined;
  readonly classOverrides?: DocPackReadingClassOverrides | undefined;
  readonly splitAtSkippedSegments?: boolean | undefined;
};

export const usableDocPack = (
  docPack: DocPackInfo | undefined | null,
  {
    enabled,
    downloaded,
  }: { enabled: boolean; downloaded: DownloadedAudio | undefined },
) => {
  // A download plays in the format it was downloaded in: a doc-pack download
  // keeps no chapter audio on disk, so the switch must not send it back to
  // chapters, and a chapters download has no pack to switch to.
  const source = downloaded
    ? downloaded.docPack
    : enabled
      ? docPack
      : undefined;
  return source && isDocPackVersionUsable(source) ? source : undefined;
};

export type ReaderSourceOverride = 'auto' | 'doc-pack' | 'chapters';

const readerSourceOverrideAtom = Atom.make<ReaderSourceOverride>('auto').pipe(
  Atom.keepAlive,
);

export const useReaderSourceOverride = () =>
  useAtomValue(readerSourceOverrideAtom);

export const useSetReaderSourceOverride = () =>
  useAtomSet(readerSourceOverrideAtom);

export const docPackEnabledWithOverride = (
  override: ReaderSourceOverride,
  gate: boolean,
) => (override === 'auto' ? gate : override === 'doc-pack');

const docPackActiveAudioIdAtom = Atom.make<string | null>(null).pipe(
  Atom.keepAlive,
);

export function useDocPackActiveStore<T>(
  selector: (state: {
    activeAudioId: string | null;
    setActive: (audioId: string | null) => void;
  }) => T,
): T {
  const setActive = useAtomSet(docPackActiveAudioIdAtom);
  // Derived-atom routing: gated on the selected value (zustand parity).
  return useAtomValue(docPackActiveAudioIdAtom, (activeAudioId) =>
    selector({ activeAudioId, setActive }),
  );
}
